import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Microphone, Stop, Sparkle, ArrowRight, X, Hammer, ListChecks } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import VoiceToPunchModal from "@/components/VoiceToPunchModal";

// Renders as AdminShell wrapper unless embedded (from AskRadar hub tabs).
const Shell = ({ embedded, children }) => embedded ? <>{children}</> : <AdminShell>{children}</AdminShell>;

export default function AdminVoiceToJob({ embedded = false }) {
  const [mode, setMode] = useState("job"); // "job" | "project"
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dropping, setDropping] = useState(false);
  const [projects, setProjects] = useState([]);
  const [targetProjectId, setTargetProjectId] = useState("");
  const [multiUnitHint, setMultiUnitHint] = useState(false);
  const [showPunchModalFor, setShowPunchModalFor] = useState(null); // project object
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    // Lazy-load projects only when the user flips into project mode
    if (mode === "project" && projects.length === 0) {
      api.get("/projects").then(r => setProjects(r.data?.projects || [])).catch(() => setProjects([]));
    }
  }, [mode, projects.length]);

  const start = async () => {
    if (!navigator.mediaDevices) { toast.error("Microphone not supported"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => stream.getTracks().forEach(t => t.stop());
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error("Mic permission denied"); }
  };

  const stop = async () => {
    const mr = mediaRef.current;
    if (!mr) return;
    mr.stop();
    setRecording(false);
    await new Promise(r => setTimeout(r, 300));
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      const r = await api.post("/ai/voice-to-job", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(r.data);
      const tx = r.data?.transcript || "";
      // Ask the backend if this smells like a multi-unit list
      if (tx) {
        try {
          const hint = await api.post("/ai/voice-to-project-hint", { text: tx });
          setMultiUnitHint(!!hint.data?.multi_unit);
        } catch { setMultiUnitHint(false); }
      }
      if (!r.data?.extracted?.property_address) toast.error("Radar couldn't hear an address — try again");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcribe failed"); }
    finally { setBusy(false); }
  };

  const drop = async () => {
    const ex = result?.extracted;
    if (!ex?.property_address) return;
    setDropping(true);
    try {
      const r = await api.post("/parking-lot", {
        property_address: ex.property_address, unit: ex.unit || "",
        quick_note: [ex.title, ex.note].filter(Boolean).join(" · "),
      });
      toast.success(`Dropped to ClosureRadar™ · WO ${r.data.work_order_number || r.data.id}`);
      setResult(null); setMultiUnitHint(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Drop failed"); }
    finally { setDropping(false); }
  };

  const jumpToProjectMode = () => {
    setMode("project"); setResult(null); setMultiUnitHint(false);
    toast.message("Switched to Project mode — pick a project, then tap the mic.");
  };

  const openPunchModalForSelected = () => {
    const p = projects.find(x => (x.id || x._id) === targetProjectId);
    if (!p) { toast.error("Pick a project first"); return; }
    setShowPunchModalFor(p);
  };

  return (
    <Shell embedded={embedded}>
      <div className="max-w-2xl mx-auto px-4 py-6" data-testid="voice-to-job-page">
        <div className="flex items-center gap-3">
          <Microphone weight="fill" size={30} className="text-fuchsia-600"/>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
            Ask Radar · Voice
          </h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-xl">
          One-shot single job → ClosureRadar™. Multi-unit walkthrough list → straight into a ProjectRadar punch list.
        </p>

        {/* Mode toggle */}
        <div className="mt-5 inline-flex border-2 border-[#0f172a] bg-white" data-testid="voice-mode-toggle">
          <button onClick={() => setMode("job")} data-testid="voice-mode-job"
            className={`px-3 h-10 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${
              mode === "job" ? "bg-[#00C4CC] text-white" : "text-slate-700 hover:bg-slate-50"
            }`}>
            <Hammer weight="bold" size={13}/> Single Job
          </button>
          <button onClick={() => setMode("project")} data-testid="voice-mode-project"
            className={`px-3 h-10 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${
              mode === "project" ? "bg-fuchsia-600 text-white" : "text-slate-700 hover:bg-slate-50"
            }`}>
            <ListChecks weight="bold" size={13}/> Project Punch List
          </button>
        </div>

        {/* SINGLE-JOB MODE */}
        {mode === "job" && (
          <>
            <div className="mt-8 flex flex-col items-center" data-testid="voice-recorder">
              {!recording ? (
                <button onClick={start} disabled={busy} data-testid="voice-start"
                  className="h-32 w-32 bg-fuchsia-600 text-white rounded-full flex flex-col items-center justify-center hover:opacity-90 disabled:opacity-40 shadow-2xl">
                  <Microphone weight="fill" size={48}/>
                  <span className="text-[10px] font-black uppercase tracking-widest mt-1">Tap to Talk</span>
                </button>
              ) : (
                <button onClick={stop} data-testid="voice-stop"
                  className="h-32 w-32 bg-red-600 text-white rounded-full flex flex-col items-center justify-center animate-pulse shadow-2xl">
                  <Stop weight="fill" size={48}/>
                  <span className="text-[10px] font-black uppercase tracking-widest mt-1">Stop</span>
                </button>
              )}
              <div className="mt-3 text-xs font-black uppercase tracking-widest text-slate-500">
                {recording ? "Recording… tap to stop" : busy ? "Radar is listening…" : "Say one job · Radar handles the rest"}
              </div>
            </div>

            {multiUnitHint && result?.transcript && (
              <div className="mt-6 p-3 bg-amber-50 border-l-4 border-amber-500" data-testid="voice-multi-unit-hint">
                <div className="text-[11px] font-black uppercase tracking-widest text-amber-800">Radar heard multiple units</div>
                <div className="mt-1 text-sm text-amber-900">
                  This looks like a walkthrough list, not a single job. Want to send it into a ProjectRadar punch list instead?
                </div>
                <button onClick={jumpToProjectMode} data-testid="voice-jump-to-project"
                  className="mt-2 h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest">
                  Switch to Project Mode →
                </button>
              </div>
            )}

            {result && (
              <div className="mt-8 rugged-card p-5" data-testid="voice-result">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkle size={14} weight="fill" className="text-fuchsia-600"/>
                  <span className="text-xs font-black uppercase tracking-widest">Heard</span>
                  <button onClick={() => { setResult(null); setMultiUnitHint(false); }}
                    className="ml-auto text-slate-400 hover:text-slate-700"><X size={16}/></button>
                </div>
                <div className="text-sm italic text-slate-700 p-3 bg-slate-50 border-l-4 border-fuchsia-500 mb-4" data-testid="voice-transcript">
                  "{result.transcript}"
                </div>
                {result.extracted?.property_address ? (
                  <div className="space-y-2" data-testid="voice-extracted">
                    <Field label="Property" value={`${result.extracted.property_address}${result.extracted.unit ? ` · Unit ${result.extracted.unit}` : ""}`}/>
                    <Field label="Task" value={result.extracted.title}/>
                    {result.extracted.priority && result.extracted.priority !== "normal" && (
                      <Field label="Priority" value={result.extracted.priority.toUpperCase()} highlight={result.extracted.priority === "urgent"}/>
                    )}
                    {result.extracted.tenant_name && <Field label="Tenant" value={result.extracted.tenant_name}/>}
                    {result.extracted.note && <Field label="Notes" value={result.extracted.note}/>}
                    <button onClick={drop} disabled={dropping} data-testid="voice-drop-btn"
                      className="mt-4 w-full h-12 bg-emerald-600 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-40">
                      {dropping ? "Dropping…" : "Drop To ClosureRadar™"} <ArrowRight size={12} weight="bold"/>
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 italic">No address heard — say the property address clearly and try again.</div>
                )}
              </div>
            )}
          </>
        )}

        {/* PROJECT-PUNCH MODE */}
        {mode === "project" && (
          <div className="mt-6" data-testid="voice-project-mode">
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Target Project</label>
            <select value={targetProjectId} onChange={e => setTargetProjectId(e.target.value)}
              data-testid="voice-project-picker"
              className="w-full h-11 px-3 border-2 border-[#0f172a] font-bold text-sm bg-white">
              <option value="">— pick a ProjectRadar project —</option>
              {projects.map(p => (
                <option key={p.id || p._id} value={p.id || p._id}>
                  {p.name} · {p.property_address}
                </option>
              ))}
            </select>
            <button onClick={openPunchModalForSelected} disabled={!targetProjectId}
              data-testid="voice-open-punch-modal"
              className="mt-4 w-full h-14 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 shadow-xl">
              <Microphone weight="fill" size={18}/> Open Voice Recorder
            </button>
            <p className="mt-3 text-xs text-slate-600">
              Radar will listen, split your dictation into per-unit punch items, and let you review + edit before saving to the project.
            </p>
          </div>
        )}

        {showPunchModalFor && (
          <VoiceToPunchModal
            projectId={showPunchModalFor.id || showPunchModalFor._id}
            projectName={showPunchModalFor.name}
            rooms={showPunchModalFor.rooms || []}
            onClose={() => setShowPunchModalFor(null)}
            onAdded={() => setShowPunchModalFor(null)}
          />
        )}
      </div>
    </Shell>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-bold ${highlight ? "text-red-600" : ""}`}>{value}</div>
    </div>
  );
}
